import { useCallback, useEffect } from 'react';
import { GoogleTokenConfig } from './cfg';
import { RootState } from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';
import { addSpreadsheetData } from '../../lib/redux/sheets/slice';

const TRELLUS_COLUMNS = [
  'Do not call again (Trellus)',
  'TRELLUS_Last_Call_Attempt',
  'TRELLUS_Last_Call_Outcome',
  'TRELLUS_Last_Time_Human',
  'TRELLUS_Total_Call_Attempts',
  'TRELLUS_Call_Notes',
  'TRELLUS_All_Previous_Calls'
];

// Track which sheet GIDs already have Trellus columns to avoid re-adding
const TRELLUS_COLUMNS_ADDED_KEY = 'trellus_columns_added';

function getTrellusColumnsAdded() {
  const saved = localStorage.getItem(TRELLUS_COLUMNS_ADDED_KEY);
  if (!saved) return {};
  try { return JSON.parse(saved); }
  catch (error) { return {}; }
}

function setTrellusColumnsAdded(sheetGid: string) {
  const current = getTrellusColumnsAdded();
  current[sheetGid] = true;
  try { localStorage.setItem(TRELLUS_COLUMNS_ADDED_KEY, JSON.stringify(current)); }
  catch (error) {}
}

function hasColumnsAdded(sheetGid: string): boolean {
  const added = getTrellusColumnsAdded();
  return added[sheetGid] === true;
}

// Helper function to find the correct sheet by gid or fall back to first sheet
function findSheetByGid(sheets: any[], gid?: string) {
  if (!sheets || sheets.length === 0) return null;
  
  if (gid) {
    const targetSheet = sheets.find(sheet => sheet.properties?.sheetId?.toString() === gid);
    if (targetSheet) return targetSheet;
  }
  
  // Fall back to first sheet
  return sheets[0];
}

export function useEnsureTrellusColumns() {
  const dispatch = useDispatch();
  const { makeApiCall } = useSpreadsheetApiCall();
  
  // Remove automatic triggering - now only called manually when needed

  const ensureTrellusColumns = useCallback(async (spreadsheetId: string, sheetGid?: string): Promise<boolean> => {
    try {
      console.log(`🔍 [ensureTrellusColumns] Starting column check for spreadsheet: ${spreadsheetId}, sheetGid: ${sheetGid}`);
      
      if (!spreadsheetId) {
        console.error(`❌ [ensureTrellusColumns] No spreadsheetId provided`);
        return false;
      }
      
      // Check if we already added columns to this sheet (but still validate actual headers)
      const columnsMarkedAsAdded = sheetGid && hasColumnsAdded(sheetGid);
      if (columnsMarkedAsAdded) {
        console.log(`🔍 [ensureTrellusColumns] LocalStorage says columns already added to sheet ${sheetGid}, but validating actual headers...`);
      }
      
      const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title,sheets.properties`;
      const metadataResult = await makeApiCall({ url: metadataUrl });
      
      if (!metadataResult.success || !metadataResult.data) {
        console.error(`❌ [ensureTrellusColumns] Failed to fetch spreadsheet metadata`, { spreadsheetId, success: metadataResult.success, hasData: !!metadataResult.data });
        return false;
      }
      
      const metadata = metadataResult.data;
      const targetSheet = findSheetByGid(metadata.sheets || [], sheetGid);
      if (!targetSheet) {
        console.error(`❌ [ensureTrellusColumns] No target sheet found`, { 
          spreadsheetId, 
          sheetGid, 
          availableSheets: metadata.sheets?.map((s: any) => ({ title: s.properties?.title, id: s.properties?.sheetId })) || [] 
        });
        return false;
      }
      
      const sheetId = targetSheet.properties.sheetId;
      const sheetTitle = targetSheet.properties.title;
      const actualSheetGid = sheetId.toString();

      // Get current header row
      const headerUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}!1:1`;
      const headerResult = await makeApiCall({ url: headerUrl });
      
      if (!headerResult.success || !headerResult.data) {
        console.error(`❌ [ensureTrellusColumns] Failed to fetch header row`, { 
          spreadsheetId, 
          sheetTitle, 
          success: headerResult.success, 
          hasData: !!headerResult.data 
        });
        return false;
      }

      const currentHeaders = headerResult.data.values?.[0] || [];
      if (currentHeaders.length === 0) {
        console.error(`❌ [ensureTrellusColumns] No headers found in sheet`, { spreadsheetId, sheetTitle });
        return false;
      }
      
      console.log(`📋 [ensureTrellusColumns] Current headers:`, currentHeaders);
      
      const missingColumns = TRELLUS_COLUMNS.filter(column => !currentHeaders.includes(column));
      
      console.log(`🔍 [ensureTrellusColumns] Missing columns:`, missingColumns);
      
      if (missingColumns.length === 0) {
        console.log(`✅ [ensureTrellusColumns] All TRELLUS columns already exist`);
        // Make sure localStorage is in sync
        if (actualSheetGid) {
          setTrellusColumnsAdded(actualSheetGid);
        }
        return true;
      }
      
      // If localStorage said columns were added but they're missing, clear the cache and log the issue
      if (columnsMarkedAsAdded) {
        console.error(`🚨 [ensureTrellusColumns] LocalStorage cache was incorrect! Columns were marked as added but ${missingColumns.length} columns are missing. Clearing cache.`, {
          sheetGid,
          missingColumns,
          currentHeaders
        });
        
        // Clear the incorrect cache entry
        const current = getTrellusColumnsAdded();
        delete current[sheetGid!];
        try { 
          localStorage.setItem(TRELLUS_COLUMNS_ADDED_KEY, JSON.stringify(current)); 
        } catch (error) {
          console.error(`❌ [ensureTrellusColumns] Failed to clear localStorage cache:`, error);
        }
      }

      // Track if we need to refresh cached data (when columns are added/inserted)
      let needsDataRefresh = false;

      // Handle TRELLUS_Remove insertion at the front (requires inserting a new column)
      const trellusRemoveIndex = missingColumns.indexOf('Do not call again (Trellus)');
      let batchRequests = [];
      
      if (trellusRemoveIndex !== -1) {
        // Insert a new column at the beginning (position 0)
        batchRequests.push({
          insertDimension: {
            range: {
              sheetId: sheetId,
              dimension: 'COLUMNS',
              startIndex: 0,
              endIndex: 1
            }
          }
        });
        
        // Remove TRELLUS_Remove from missingColumns since we're handling it separately
        missingColumns.splice(trellusRemoveIndex, 1);
        needsDataRefresh = true; // Column insertion shifts all data
      }
      
      // If we need to insert TRELLUS_Remove column, do the batch update first
      if (batchRequests.length > 0) {
        const batchUpdateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`;
        const batchResult = await makeApiCall({
          url: batchUpdateUrl,
          method: 'POST',
          body: { requests: batchRequests }
        });
        
        if (!batchResult.success) {
          console.error(`❌ [ensureTrellusColumns] Failed to insert TRELLUS_Remove column`, { spreadsheetId, sheetTitle });
          return false;
        }
        
        console.log(`✅ [ensureTrellusColumns] Successfully inserted TRELLUS_Remove column`);
      }
      
      // Now update the header row with proper headers
      let newHeaders = [...currentHeaders];
      
      if (trellusRemoveIndex !== -1) {
        // TRELLUS_Remove goes in the first column, existing headers shift right
        newHeaders = ['Do not call again (Trellus)', ...currentHeaders];
      }
      
      // Add any other missing columns at the end
      if (missingColumns.length > 0) {
        newHeaders = [...newHeaders, ...missingColumns];
        needsDataRefresh = true; // New columns added
      }
      
      // Update the header row
      const updateUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}!1:1?valueInputOption=RAW`;
      const updateResult = await makeApiCall({
        url: updateUrl,
        method: 'PUT',
        body: { values: [newHeaders] }
      });

      if (!updateResult.success) {
        console.error(`❌ [ensureTrellusColumns] Failed to update header row`, { 
          spreadsheetId, 
          sheetTitle, 
          newHeaders: newHeaders.length 
        });
        return false;
      }
      
      console.log(`✅ [ensureTrellusColumns] Successfully updated headers with ${missingColumns.length + (trellusRemoveIndex !== -1 ? 1 : 0)} new columns`);

      // If we made structural changes (added/inserted columns), refresh the cached data
      if (needsDataRefresh) {
        // Fetch updated spreadsheet data
        const dataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetTitle)}`;
        const dataResult = await makeApiCall({ url: dataUrl });
        
        if (dataResult.success && dataResult.data) {
          // Update the cached data in Redux
          const updatedSpreadsheetData = {
            id: metadata.spreadsheetId,
            name: metadata.properties.title,
            sheets: metadata.sheets.map((sheet: any) => ({
              title: sheet.properties.title,
              sheetId: sheet.properties.sheetId,
              rowCount: sheet.properties.gridProperties?.rowCount || 0,
              columnCount: sheet.properties.gridProperties?.columnCount || 0,
            })),
            data: dataResult.data.values || [],
          };
          
          dispatch(addSpreadsheetData(updatedSpreadsheetData));
        }
      }

      // Mark this sheet as having columns added
      if (actualSheetGid) {
        setTrellusColumnsAdded(actualSheetGid);
        console.log(`✅ [ensureTrellusColumns] Marked sheet ${actualSheetGid} as having TRELLUS columns`);
      }
      
      console.log(`✅ [ensureTrellusColumns] Successfully ensured all TRELLUS columns exist`);
      return true;
    } catch (error) {
      console.error(`❌ [ensureTrellusColumns] Unexpected error:`, error);
      console.error(`❌ [ensureTrellusColumns] Error details:`, { 
        spreadsheetId, 
        sheetGid, 
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      return false;
    }
  }, [makeApiCall, dispatch])

  return { ensureTrellusColumns };
} 